/** Settings, in the same panes as every page (2026-10-02): the settings
 *  pages and Mail's sections in the nav pane, the page itself in one
 *  content pane. A layout route: the settings page renders in <Outlet />. */
import { Suspense, type ReactNode } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Activity, Calendar as CalendarIcon, Cpu, Database, PenLine } from "lucide-react";
import { NavRow } from "./NavRow";
import { NavGroup, NavPane, NavPaneHeader, NavPaneStrip, PaneShell, READ_PANE_CLASS, useColumnWidths, useNavPane } from "./Panes";
import { PageSkeleton } from "./Skeleton";
import { MAIL_SETTINGS_SECTIONS, mailSettingsSection } from "../pages/mailSettingsSections";

type Row = { id: string; label: string; icon: ReactNode; to: string; active: boolean };

export function SettingsShell() {
  const cols = useColumnWidths();
  const navPane = useNavPane();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const onMail = pathname === "/settings/mail";
  const section = mailSettingsSection(new URLSearchParams(search).get("section"));

  const mail: Row[] = [
    ...MAIL_SETTINGS_SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      icon: <s.icon className="h-3.5 w-3.5" />,
      to: `/settings/mail?section=${s.id}`,
      active: onMail && section === s.id,
    })),
    { id: "signatures", label: "Signatures", icon: <PenLine className="h-3.5 w-3.5" />, to: "/settings/mail/signatures", active: pathname === "/settings/mail/signatures" },
    { id: "cache", label: "Mail cache", icon: <Database className="h-3.5 w-3.5" />, to: "/settings/mail/cache", active: pathname === "/settings/mail/cache" },
    { id: "wov-jobs", label: "WOV jobs", icon: <Activity className="h-3.5 w-3.5" />, to: "/mail/jobs", active: pathname === "/mail/jobs" },
  ];
  const other: Row[] = [
    { id: "calendar", label: "Calendar", icon: <CalendarIcon className="h-3.5 w-3.5" />, to: "/settings/calendar", active: pathname === "/settings/calendar" },
    { id: "ai-routing", label: "AI models", icon: <Cpu className="h-3.5 w-3.5" />, to: "/settings/ai", active: pathname.startsWith("/settings/ai") },
  ];

  const row = (r: Row) => <NavRow key={r.id} icon={r.icon} label={r.label} selected={r.active} onClick={() => navigate(r.to)} />;

  return (
    <PaneShell>
      <NavPane
        pane={navPane}
        width={cols.navW}
        setWidth={cols.setNavW}
        resetWidth={cols.resetNav}
        label="Resize the settings list"
        header={<NavPaneHeader title="Settings" pane={navPane} what="settings" />}
        strip={
          <NavPaneStrip
            items={[...mail, ...other].map((r) => ({ id: r.id, icon: r.icon, title: r.label, active: r.active, onClick: () => navigate(r.to) }))}
            pane={navPane}
            what="settings"
          />
        }
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-1.5">
          <NavGroup title="Mail">{mail.map(row)}</NavGroup>
          <NavGroup title="Calendar and AI">{other.map(row)}</NavGroup>
        </div>
      </NavPane>
      <section className={"flex flex-1 min-w-0 min-h-0 flex-col overflow-y-auto " + READ_PANE_CLASS}>
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </section>
    </PaneShell>
  );
}
